// src/plugins/sales-forecast/components/MarketForecastPanel.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useConfig, useElementData, useElementColumns } from "@sigmacomputing/plugin";
import type { Params } from "./ControlPanel";
import type { EhRow } from "../lib/useSigmaElementData";
import type { CloneEntry } from "./CloneNeighborhoodControl";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

const api_base = process.env.REACT_APP_API_BASE;

/* --------------------------- types --------------------------- */
export type ForecastRecord = {
  date: string;
  presolds: number;
  sold_specs: number;
  unsold_specs: number;
  available_lots: number;
};

export type ServerComposition = {
  spec_pct?: number | string | null;
  presold_pct?: number | string | null;
  specPct?: number | string | null;
  presoldPct?: number | string | null;
  comp_fraction?: number | string | null;
  compFraction?: number | string | null;
  cluster_key?: string | number | null;
  clusterKey?: string | number | null;
} | null;

export interface MarketForecastPanelProps {
  params: Params;
  cluster: number;
  trigger: boolean;
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  setForecastData: React.Dispatch<React.SetStateAction<ForecastRecord[]>>;
  setComposition?: React.Dispatch<React.SetStateAction<ServerComposition>>;
  domPredictions: { [key: string]: number | null } | null;
  elementRows: EhRow[];
  selectedClone: CloneEntry | null;
}

/* --------------------------- utils --------------------------- */
function deepEqual(a: any, b: any): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function shallowEqual(a: any, b: any): boolean {
  if (a === b) return true;
  const A = Object.keys(a || {});
  const B = Object.keys(b || {});
  if (A.length !== B.length) return false;
  return A.every((k) => (a as any)[k] === (b as any)[k]);
}

function toIsoOrNull(v: unknown): string | null {
  if (!v) return null;
  try {
    const d = v instanceof Date ? v : new Date(v as any);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

/* --------------------------- component --------------------------- */
export const MarketForecastPanel: React.FC<MarketForecastPanelProps> = ({
  params,
  cluster,
  trigger,
  setTrigger,
  setForecastData,
  setComposition,
  domPredictions,
  elementRows,
  selectedClone,
}) => {
  console.log("[MarketForecastPanel] selectedClone:", selectedClone?.id, selectedClone?.title);

  const theme = useSystemTheme();

  // 🎨 Theme colors
  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const mutedText = css.getPropertyValue("--muted-text-color").trim();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastDataRef = useRef<ForecastRecord[] | null>(null);
  const lastParamsRef = useRef<Params>(params);
  const lastDomRef = useRef<typeof domPredictions>(domPredictions);

  const cfg = useConfig() as any;
  const elementId: string = (cfg?.ehMaster ?? cfg?.dataSource ?? "") as string;
  const rawDataAny = useElementData(elementId) as Record<string, any[]> | undefined;
  useElementColumns(elementId);

  /* --------------------------- data extraction --------------------------- */
  const directRows: EhRow[] = useMemo(() => {
    if (!rawDataAny || typeof rawDataAny !== "object") return [];
  
    // 👇 ADD THESE DEBUG LINES
    console.log("[DEBUG] rawDataAny keys:", Object.keys(rawDataAny));
    console.log("[DEBUG] cfg mapping:", {
      priceCol: cfg?.priceCol,
      sqftCol: cfg?.sqftCol,
      styleCol: cfg?.styleCol,
      floorPlanCol: cfg?.floorPlanCol,
    });
    // 👆 This will print the real data keys and your config mapping
  
    const idAddress         = (cfg?.addressCol           ?? null) as string | null;
    const idCity            = (cfg?.cityCol              ?? null) as string | null;
    const idNeigh           = (cfg?.neighborhoodCol      ?? null) as string | null;
    const idHouseType       = (cfg?.houseTypeCol         ?? null) as string | null;
    const idContractDate    = (cfg?.contractDateCol      ?? null) as string | null;
    const idGreenLight      = (cfg?.officeGreenLightCol  ?? null) as string | null;
    const idActiveComps     = (cfg?.activeCompsCol       ?? null) as string | null;
    const idMarket          = (cfg?.marketCol            ?? null) as string | null;
    const idSchoolDistrict  = (cfg?.schoolDistrictCol    ?? null) as string | null;
    const idFloorPlan       = (cfg?.floorPlanCol         ?? null) as string | null;
    const idStyle           = (cfg?.styleCol             ?? null) as string | null;
    const idPrice           = (cfg?.priceCol             ?? null) as string | null;
    const idSQFT            = (cfg?.sqftCol              ?? null) as string | null;

    const pick = (id: string | null, i: number) =>
      id && Array.isArray(rawDataAny[id]) ? rawDataAny[id][i] : null;

    const idForLen =
      idAddress || idCity || idNeigh || idHouseType || idContractDate || idGreenLight || idActiveComps || idMarket || idSchoolDistrict;
    if (!idForLen || !Array.isArray(rawDataAny[idForLen])) return [];

    const len = rawDataAny[idForLen].length;
    const out: EhRow[] = new Array(len);
    for (let i = 0; i < len; i++) {
      const dt = pick(idContractDate, i);
      const dt2 = pick(idGreenLight, i);
      const ac = pick(idActiveComps, i);
      const pc = pick(idPrice, i);
      const sq = pick(idSQFT, i);
      out[i] = {
        address:          pick(idAddress, i),
        city:             pick(idCity, i),
        neighborhood:     pick(idNeigh, i),
        houseType:        pick(idHouseType, i),
        contractDate:     dt ? toIsoOrNull(dt) : null,
        officeGreenLight: dt2 ? toIsoOrNull(dt2) : null,
        activeComps:      ac === null || ac === undefined ? null : Number(ac),
        market:           pick(idMarket, i),
        schoolDistrict:   pick(idSchoolDistrict, i),
        floorPlan:        pick(idFloorPlan, i),
        style:            pick(idStyle, i),
        price:            pc === null || pc === undefined ? null : Number(pc),
        sqft:             sq === null || sq === undefined ? null : Number(sq)
      };
    }
    return out;
  }, [
    rawDataAny,
    cfg?.addressCol,
    cfg?.cityCol,
    cfg?.neighborhoodCol,
    cfg?.houseTypeCol,
    cfg?.contractDateCol,
    cfg?.officeGreenLightCol,
    cfg?.activeCompsCol,
    cfg?.marketCol,
    cfg?.schoolDistrictCol,
    cfg?.floorPlanCol,
    cfg?.styleCol,
    cfg?.priceCol,
    cfg?.sqftCol
  ]);

  const rows: EhRow[] = useMemo(
    () => (Array.isArray(elementRows) && elementRows.length ? elementRows : directRows),
    [elementRows, directRows]
  );

  /* --------------------------- debug hooks --------------------------- */
  useEffect(() => {
    const first = rows.length ? rows[0] : undefined;
    console.log("[SF] elementRows (effective):", rows.length, first);
  }, [rows]);

  useEffect(() => {
    if (!shallowEqual(params, lastParamsRef.current)) lastParamsRef.current = params;
  }, [params]);

  useEffect(() => {
    if (!deepEqual(domPredictions, lastDomRef.current)) lastDomRef.current = domPredictions;
  }, [domPredictions]);

  /* --------------------------- identifiers --------------------------- */
  const paramNeighborhoodID = (params as any)?.neighborhoodID ?? null;
  const paramNeighborhoodTitle = (params as any)?.neighborhoodTitle ?? null;

  const effectiveCloneId = useMemo(
    () => selectedClone?.id ?? paramNeighborhoodID,
    [selectedClone?.id, paramNeighborhoodID]
  );
  const effectiveCloneTitle = useMemo(
    () => selectedClone?.title ?? paramNeighborhoodTitle,
    [selectedClone?.title, paramNeighborhoodTitle]
  );

  /* --------------------------- forecast execution --------------------------- */
  useEffect(() => {
    console.log("[SF] useEffect fired", {
      trigger,
      rows: rows.length,
      cluster,
      hasDom: !!lastDomRef.current,
      effectiveCloneId,
      effectiveCloneTitle,
    });

    if (!trigger) return;
    if (rows.length === 0) {
      console.warn("[SF] waiting: elementRows not ready yet; will try again when rows change");
      return;
    }

    const runForecast = async () => {
      setLoading(true);
      setError(null);
      setComposition?.(null);

      const payload: any = {
        ...lastParamsRef.current,
        cluster,
        compositionPct: Number(lastParamsRef.current.compositionPct) / 100,
        cloneDate: lastParamsRef.current.cloneDate
          ? new Date(lastParamsRef.current.cloneDate).toISOString().split("T")[0]
          : null,
      };

      console.log("[SF] cloneDate being sent →", payload.cloneDate);

      const sigmaElementRows: EhRow[] = rows.map((r) => ({
        address:          r.address ?? null,
        city:             r.city ?? null,
        neighborhood:     r.neighborhood ?? null,
        houseType:        r.houseType ?? null,
        contractDate:     toIsoOrNull(r.contractDate),
        officeGreenLight: toIsoOrNull(r.officeGreenLight),
        activeComps:      r.activeComps === null || r.activeComps === undefined ? null : Number(r.activeComps),
        market:           r.market ?? null,
        schoolDistrict:   r.schoolDistrict ?? null,
        floorPlan:        r.floorPlan ?? null,
        style:            r.style ?? null,
        price:            r.price === null || r.price === undefined ? null : Number(r.price),
        sqft:             r.sqft === null || r.sqft === undefined ? null : Number(r.sqft),
      }));

      payload.sigmaElementRows = sigmaElementRows;
      payload.cloneNeighborhoodId = effectiveCloneId;
      payload.cloneNeighborhoodTitle = effectiveCloneTitle;
      payload.sigmaInfo = {
        fields: [
          "address",
          "city",
          "neighborhood",
          "houseType",
          "contractDate",
          "officeGreenLight",
          "activeComps",
          "market",
          "schoolDistrict",
          "floorPlan",
          "style",
          "price",
          "sqft"
        ],
        rowCount: sigmaElementRows.length,
        sample: sigmaElementRows.length ? sigmaElementRows[0] : null,
      };

      if (lastDomRef.current) {
        payload.dom_85pct = lastDomRef.current.dom_85pct ?? null;
        payload.dom_90pct = lastDomRef.current.dom_90pct ?? null;
        payload.dom_95pct = lastDomRef.current.dom_95pct ?? null;
        payload.dom_100pct = lastDomRef.current.dom_100pct ?? null;
        payload.domFromClient = true;
      }

      const url = `${api_base}/predict-forecast`;
      console.log("[SF] POST → /predict-forecast", {
        url,
        rowCount: sigmaElementRows.length,
        firstRow: sigmaElementRows[0],
      });

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          let details = "";
          try {
            const errJson = await response.json();
            details = typeof errJson?.error === "string" ? ` — ${errJson.error}` : "";
          } catch {}
          throw new Error(`Error ${response.status}: ${response.statusText}${details}`);
        }

        const respJson = await response.json();
        let timeline: ForecastRecord[] = [];
        let serverComp: ServerComposition = null;

        if (Array.isArray(respJson)) {
          timeline = respJson as ForecastRecord[];
        } else {
          timeline = (respJson?.timeline ?? []) as ForecastRecord[];
          if (respJson?.composition && typeof respJson.composition === "object") {
            serverComp = {
              spec_pct: respJson.composition.spec_pct,
              presold_pct: respJson.composition.presold_pct,
              comp_fraction: respJson.composition.comp_fraction,
              specPct: respJson.composition.specPct,
              presoldPct: respJson.composition.presoldPct,
              compFraction: respJson.composition.compFraction,
              cluster_key: respJson.cluster_key ?? undefined,
              clusterKey: respJson.cluster_key ?? undefined,
            };
            console.log("[SF] composition from server (forwarded):", serverComp);
          }
        }

        if (!deepEqual(lastDataRef.current, timeline)) {
          lastDataRef.current = timeline;
          setForecastData([...timeline]);
        } else {
          console.log("[SF] no change in forecast data, skipping update.");
        }

        if (setComposition) setComposition(serverComp);
      } catch (err: any) {
        console.error("[SF] forecast call failed:", err);
        setError(err?.message || "Unknown error occurred");
        setForecastData([]);
        setComposition?.(null);
      } finally {
        setLoading(false);
        setTrigger(false);
      }
    };

    runForecast();
  }, [trigger, cluster, rows, effectiveCloneId, effectiveCloneTitle, setForecastData, setTrigger, setComposition]);

  /* --------------------------- render --------------------------- */
  return (
    <div className={`theme-${theme} mt-6 w-full`} style={{ color: textColor, backgroundColor: "transparent" }}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-3xl font-bold" style={{ color: textColor }}>
          Market Forecast
        </h2>
      </div>

      {loading && (
        <p className="text-sm text-center italic" style={{ color: mutedText }}>
          Loading forecast data...
        </p>
      )}

      {!loading && error && (
        <p className="text-sm text-center" style={{ color: "var(--error-text)" }}>
          Error: {error}
        </p>
      )}

      {!loading && !error && (
        <p className="text-center text-sm" style={{ color: mutedText }}>
          Forecast data loaded and ready for visualization.
        </p>
      )}
    </div>
  );
};